import {
  ErrorState,
  Panel,
  Progress,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
} from '@/design/components';
import { formatDate } from '@/lib/dates';
import { useQuestionStats, useReport } from './api';

/**
 * דוח ההשלמה של שיוך (PRD 5.6.4): מי סיים, מי באיחור, ואיזו שאלה
 * מפילה את כולם.
 *
 * הקהל מחושב מחדש בכל טעינה, ולכן עובד שעבר תפקיד נכנס או יוצא
 * מהרשימה מעצמו. הדוח משקף את המצב היום, לא את יום השיוך.
 */
export function AssignmentReport({
  assignmentId,
  quizId,
}: {
  assignmentId: string;
  quizId: string | null;
}) {
  const report = useReport(assignmentId);
  const stats = useQuestionStats(quizId);

  if (report.isPending) return <Skeleton className="h-40" />;
  if (report.isError) return <ErrorState onRetry={() => void report.refetch()} />;
  if (!report.data) return null;

  const data = report.data;
  const overdue = data.due_date !== null && new Date(`${data.due_date}T23:59:59`) < new Date();

  return (
    <>
      <Panel title="מי השלים">
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <Progress
              value={data.done}
              max={data.total}
              label={`${data.done} מתוך ${data.total} השלימו`}
              className="min-w-48 grow"
            />
            {data.late > 0 && <StatusBadge tone="bad">{data.late} באיחור</StatusBadge>}
            {data.due_date && (
              <span className="text-body-sm text-muted">
                להשלמה עד {formatDate(data.due_date)}
              </span>
            )}
          </div>

          {data.employees.length === 0 ? (
            <p className="m-0 text-body-sm text-muted">אין עובדים שהשיוך הזה חל עליהם היום.</p>
          ) : (
            <Table>
              <thead>
                <Tr>
                  <Th>עובד/ת</Th>
                  <Th>סניף</Th>
                  <Th>שיעורים</Th>
                  <Th>חידון</Th>
                  <Th>מצב</Th>
                </Tr>
              </thead>
              <tbody>
                {data.employees.map((row) => (
                  <Tr key={row.employee_id}>
                    <Td>{row.name}</Td>
                    <Td>{row.branch ?? '—'}</Td>
                    <Td className="tabular">
                      {row.lessons_done} / {row.lesson_count}
                    </Td>
                    <Td className="tabular">
                      {row.best_score === null ? '—' : row.best_score}
                    </Td>
                    <Td>
                      {row.done ? (
                        <StatusBadge tone="good">השלים/ה</StatusBadge>
                      ) : overdue ? (
                        <StatusBadge tone="bad">באיחור</StatusBadge>
                      ) : (
                        <StatusBadge tone="warn">בתהליך</StatusBadge>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Panel>

      {quizId && stats.data && stats.data.length > 0 && (
        <Panel title="שאלות החידון">
          <div className="grid gap-2">
            <p className="m-0 text-body-sm text-muted">
              אחוז התשובות הנכונות לכל שאלה. שאלה שכולם טועים בה היא בדרך כלל שאלה לא ברורה, לא
              עובדים שלא יודעים.
            </p>
            <Table>
              <thead>
                <Tr>
                  <Th>השאלה</Th>
                  <Th>נענתה</Th>
                  <Th>נכון</Th>
                </Tr>
              </thead>
              <tbody>
                {stats.data.map((row) => (
                  <Tr key={row.question_id}>
                    <Td>{row.prompt}</Td>
                    <Td className="tabular">{row.answered}</Td>
                    <Td>
                      {row.percent === null ? (
                        <span className="text-muted">עוד לא נענתה</span>
                      ) : row.percent < 50 ? (
                        <StatusBadge tone="bad">{row.percent}%</StatusBadge>
                      ) : (
                        <span className="tabular">{row.percent}%</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Panel>
      )}
    </>
  );
}
